import { Component, input, output, effect, signal, computed, WritableSignal, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LTPngTableComponent } from 'src/app/features/grid/components/lt-png-table/lt-png-table.component';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { GridSort, GridSelection, GridExport, GridActionColumn } from 'src/app/features/grid/classes/grid-config.types';
import { convertColumnSettingsList } from '../../../../../utilities/column-convert.util';
import { ColumnSettings } from '../../../../../models/column-settings';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-lt-generic-grid-png',
  imports: [ CommonModule, LTPngTableComponent ],
  standalone: true,
  templateUrl: './lt-generic-grid-png.html',
  styleUrl: './lt-generic-grid-png.scss',
})
export class LTGenericGridPngComponent {
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  //#region Mirror ViewGenericGrid inputs
  readonly data = input<any[]>([]);
  readonly loading = input<boolean>(false);
  readonly customExport = input<boolean>(false);
  readonly exportFileName = input<string>('Data');
  readonly columns = input<ColumnSettings[]>([]);
  readonly isReorderable = input<boolean>(true);
  readonly isFilterable = input<boolean>(true);
  readonly isResizeable = input<boolean>(false);
  readonly isSelectable = input<boolean>(true);
  readonly showSelectionColumn = input<boolean>(false);
  readonly showColumnMenu = input<boolean>(true);
  readonly selectionfield = input<string>('');
  readonly showExportToolbar = input<boolean>(true);
  readonly showChangeSelection = input<boolean>(false);
  readonly sortDescriptions = input<GridSort[]>([]);
  readonly enableAutoFit = input<boolean>(false);
  // action column props
  readonly hasActionGoToColumn = input<boolean>(false);
  readonly actionGoToUsePrefix = input<boolean>(false);
  readonly actionGoToField = input<string>('');
  readonly actionGoToPrefix = input<string>('');
  readonly actionGoToFieldAddl = input<string[]>([]);
  readonly actionGoToUseAddlField = input<boolean>(false);
  readonly actionGoToUseSuffix = input<boolean>(false);
  readonly actionGoToSuffix = input<string>('');
  // selection props
  readonly showSelectDeselectButtons = input<boolean>(false);
  readonly selectionColumnWidth = input<number>(100);
  readonly selectionColumnHeader = input<string>('Select');
  readonly overrideSelectedItem = input<any>(null);
  //#endregion

  //#region Outputs
  readonly getUpdated = output<string>();
  readonly itemSelected = output<any>();
  readonly rowSelect = output<any>();
  readonly rowUnselect = output<any>();
  readonly columnsChange = output<GridColumnData[]>();
  readonly editClicked = output<any>();
  readonly deleteClicked = output<any>();
  readonly viewClicked = output<any>();
  readonly addClicked = output<boolean>();
  readonly commandClicked = output<{ rowData: any, commandType: string, event: Event }>();
  //#endregion

  //#region Png Table Properties

  // internal signals for PrimeNG component
  private readonly gridSettingsSignal: WritableSignal<GridSettings> = signal(new GridSettings());
  private readonly showToolbarSignal: WritableSignal<boolean> = signal(true);
  private readonly preselectedItemsSignal: WritableSignal<any[]> = signal([]);
  
  // computed props
  readonly gridSettings = computed(() => this.gridSettingsSignal());
  readonly showToolbar = computed(() => this.showToolbarSignal());
  readonly preselectedItems = computed(() => this.preselectedItemsSignal());

  //#endregion

  constructor() {
    // effect to update frid when inputs change
    effect(() => {
      this.updateGridSettings();
    });

    // effect to handle pre selected items
    effect(() => {
      const selectedItem = this.overrideSelectedItem();
      if (selectedItem){
        this.preselectedItemsSignal.set([selectedItem]);
      }
    });
  }

  private updateGridSettings(): void {
    try {
      // Convert ColumnSettings to GridColumnData
      const convertedColumns = this.columns().length > 0 
        ? convertColumnSettingsList(this.columns())
        : this.buildColumnsFromData();

      // Build sort configuration
      const sort = this.buildSortConfig();
      const multisort = this.buildMultiSortConfig();
      
      // Build selection configuration
      const selection = this.buildSelectionConfig();

      // Build export configuration
      const exportConfig = this.buildExportConfig();

      // Build action column configuration
      const actionColumn = this.buildActionColumnConfig();

      // Create new GridSettings
      const newGridSettings = new GridSettings({
        data: this.data() || [],
        columns: convertedColumns,
        loading: signal(this.loading()),
        
        // Sorting
        sortMode: this.sortDescriptions().length > 1 ? 'multiple' : 'single',
        sort: sort,
        multisort: multisort,

        // Features
        resizableColumns: this.isResizeable(),
        reorderableColumns: this.isReorderable(),

        // Filter configuration
        filter: {
          enabled: this.isFilterable(),
          mode: this.isFilterable() ? (this.showColumnMenu() ? 'menu' : 'row') : null,
          display: this.isFilterable() ? (this.showColumnMenu() ? 'menu' : 'row') : null,
        },

        // Selection
        selection: selection,

        // Export
        export: exportConfig,

        // Action column
        actionColumn: actionColumn,

        // Pagination (default enabled)
        pagination: {
          enabled: true,
          rows: 10,
          rowsPerPageOptions: [5, 10, 25, 50, 100],
          showCurrentPageReport: true,
          currentPageReportTemplate: 'Showing {first} to {last} of {totalRecords} entries',
          showUpperPager: false,
        },
      });

      this.gridSettingsSignal.set(newGridSettings);
      this.showToolbarSignal.set(this.showExportToolbar() || this.showColumnMenu());
    } catch (error) {
      this.toastService.toastCreate(('Error updating grid settings:' + error), 'Warning');
      // Fallback to basic configuration
      this.gridSettingsSignal.set(new GridSettings({
        data: this.data() || [],
        columns: [],
        loading: signal(this.loading()),
      }));
    }
  }

  private buildColumnsFromData(): GridColumnData[] {
    if (!this.data() || this.data().length === 0) {
      return [];
    }

    const sampleRecord = this.data()[0];
    const fields = Object.keys(sampleRecord);
    
    return fields.map(field => new GridColumnData({
      field: field,
      header: this.formatHeader(field),
      type: this.inferColumnType(sampleRecord[field]),
      dataType: this.inferColumnDataType(sampleRecord[field]),
      display: true,
      sortable: true,
      filterable: this.isFilterable(),
      width: '200px',
    }));
  }

  private buildSortConfig(): GridSort {
    if (this.sortDescriptions().length > 0) {
      return this.sortDescriptions()[0];
    }
    return { field: null, order: 1 };
  }

  private buildMultiSortConfig(): GridSort[] {
    if (this.sortDescriptions().length > 0){
      return this.sortDescriptions()
    }
    return [];
  }

  private buildSelectionConfig(): GridSelection {
    // Selection is enabled if either isSelectable OR showSelectionColumn is true
    const selectionEnabled = this.isSelectable() || this.showSelectionColumn();
    
    // Determine selection type based on conditions
    let selectionType: 'button' | 'checkbox' | 'icon' | 'radio' | 'row';
    let iconClass = '';
    let text = this.selectionColumnHeader() || 'Select';
    
    if (!this.showSelectionColumn()) {
      // No selection column = row selection
      selectionType = 'row';
    } else if (this.showSelectDeselectButtons()) {
      // Selection column with buttons
      selectionType = 'button';
    } else {
      // Selection column with icon
      selectionType = 'icon';
      iconClass = 'pi pi-star-fill';
      text = '';
    }

    return {
      enabled: selectionEnabled,
      mode: 'single',
      selectionType: selectionType,
      showSelectAll: false,
      text: text,
      field: this.selectionfield(),
      iconClass: iconClass,
      preselectedItems: this.overrideSelectedItem() ? [this.overrideSelectedItem()] : [],
    };
  }

  private buildExportConfig(): GridExport {
    let fileName = this.exportFileName() || 'export';

    // Change .xlsx extension to .csv
    if (fileName.toLowerCase().endsWith('.xlsx')) {
      fileName = fileName.slice(0, -5) + '.csv';
    }

    return {
      enabled: this.showExportToolbar(),
      formats: ['csv'],
      fileName: fileName,
      header: 'Export Data',
      renderTemplates: true,
      defaultTemplateStrategy: 'use-raw',
    };
  }

  private buildActionColumnConfig(): GridActionColumn {
    return {
      enabled: this.hasActionGoToColumn(),
      field: this.actionGoToField(),
      usePrefix: this.actionGoToUsePrefix(),
      prefix: this.actionGoToPrefix(),
      useAddlField: this.actionGoToUseAddlField(),
      addlField: this.actionGoToFieldAddl(),
      useSuffix: this.actionGoToUseSuffix(),
      suffix: this.actionGoToSuffix(),
    };
  }

  private formatHeader(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private inferColumnType(value: any): 'text' | 'number' | 'boolean' | 'date' | 'currency' | 'custom' {
    if (value === null || value === undefined) return 'text';
    
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    if (value instanceof Date) return 'date';
    if (typeof value === 'string') {
      // Check if it looks like a date
      if (!isNaN(Date.parse(value))) return 'date';
      // Check if it looks like a number
      if (!isNaN(Number(value))) return 'number';
    }
    
    return 'text';
  }

  private inferColumnDataType(value: any): 'text' | 'number' | 'boolean' | 'date' | 'currency' | 'custom' | 'percentage' {
    const type = this.inferColumnType(value);
    return type as any; // They map 1:1 in this case
  }

  // Event handlers that maintain compatibility with ViewGenericGridComponent
  onSelectionChange(selectedRows: readonly any[]): void {
    if (selectedRows != null && selectedRows.length > 0) {
      const selectedItem = selectedRows.length > 0 ? selectedRows[0] : null;
      //this.selectedItem = selectedItem;
      this.itemSelected.emit(selectedItem);
    }
  }

  onRowSelect(event: any): void {
    this.rowSelect.emit(event);
  }

  onRowUnselect(event: any): void {
    this.rowUnselect.emit(event);
  }

  onColumnsChange(columns: GridColumnData[]): void {
    // Handle column changes - update the grid settings - does it need to?
    const currentSettings = this.gridSettingsSignal();
    const updatedSettings = new GridSettings({
      ...currentSettings,
      columns: columns,
    });
    this.gridSettingsSignal.set(updatedSettings);

    this.columnsChange.emit(columns);
  }

  onEditClicked(event: any): void {
    this.editClicked.emit(event);
  }

  onDeleteClicked(event: any): void {
    this.deleteClicked.emit(event);
  }

  onViewClicked(event: any): void {
    this.viewClicked.emit(event);
  }

  onAddClicked(event: boolean): void {
    this.addClicked.emit(event);
  }

  onCommandClicked(value: { rowData: any, commandType: string, event: Event }): void {
    this.commandClicked.emit(value);
  }

  // Public methods to maintain compatibility with ViewGenericGridComponent interface
  public getCurrentGridData(): any[] {
    return this.data() || [];
  }

  public resetSkipIndex(): void {
    // Reset pagination if needed
    const currentSettings = this.gridSettingsSignal();
    const updatedSettings = new GridSettings({
      ...currentSettings,
      pagination: {
        ...currentSettings.pagination,
        // Reset to first page
      },
    });
    this.gridSettingsSignal.set(updatedSettings);
  }

  public fitColumns(): void {
    // Maintain compatibility method
    // This would typically be handled by PrimeNG's autoLayout feature
    // Implementation depends on PrimeNG table capabilities
  }

  public saveData(component: any): void {
    // Maintain compatibility method
    // can be alternatively implemented to kick off png export - review if actually needed
  }
}
